import React, { createContext, useContext } from 'react';
import {
  SnackbarProvider as NotistackSnackbarProvider,
  useSnackbar as useNotistackSnackbar
} from 'notistack';
import { IconButton, Box } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

/**
 * Dynamic Snackbar Utility using notistack
 *
 * Features:
 * - Text or JSX messages
 * - Position: anchorOrigin { vertical: 'top'|'bottom', horizontal: 'left'|'center'|'right' }
 * - Icon: any React element
 * - Timeout: autoHideDuration in ms
 * - Color: variant 'success'|'error'|'warning'|'info'|'default'
 * - Close button: showCloseButton boolean
 * - Max snacks: maxSnack prop on provider
 *
 * Usage:
 * 1. Wrap app with <SnackbarProvider maxSnack={3}>
 * 2. Use window.showSnackbar(options) globally
 * 3. Or use useSnackbar hook in components
 *
 * Example:
 * window.showSnackbar({
 *   message: 'User saved successfully!',
 *   variant: 'success',
 *   anchorOrigin: { vertical: 'top', horizontal: 'center' },
 *   icon: <CheckCircleIcon />,
 *   autoHideDuration: 3000,
 *   showCloseButton: true
 * });
 */

// Create a context for the snackbar
const SnackbarContext = createContext();

// Custom hook to use the snackbar
export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

// Snackbar Provider Component
export const SnackbarProvider = ({ children, maxSnack = 3 }) => {
  return (
    <NotistackSnackbarProvider
      maxSnack={maxSnack}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      preventDuplicate
      autoHideDuration={4000}
    >
      <SnackbarContextProvider>{children}</SnackbarContextProvider>
    </NotistackSnackbarProvider>
  );
};

// Internal provider to provide the enqueue function
const SnackbarContextProvider = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useNotistackSnackbar();

  // Set global function
  React.useEffect(() => {
    window.showSnackbar = ({
      message,
      variant = 'default',
      anchorOrigin = { vertical: 'top', horizontal: 'right' },
      autoHideDuration = 4000,
      icon,
      showCloseButton = true,
      ...other
    }) => {
      return enqueueSnackbar(
        <Box display="flex" alignItems="center">
          {icon && <Box mr={1}>{icon}</Box>}
          {message}
        </Box>,
        {
          variant,
          anchorOrigin,
          autoHideDuration,
          action: showCloseButton
            ? (snackbarId) => (
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => closeSnackbar(snackbarId)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )
            : undefined,
          ...other
        }
      );
    };
  }, [enqueueSnackbar, closeSnackbar]);

  const showSnackbar = ({
    message,
    variant = 'default',
    anchorOrigin = { vertical: 'top', horizontal: 'right' },
    autoHideDuration = 4000,
    icon,
    showCloseButton = true,
    ...other
  }) => {
    return enqueueSnackbar(
      <Box display="flex" alignItems="center">
        {icon && <Box mr={1}>{icon}</Box>}
        {message}
      </Box>,
      {
        variant,
        anchorOrigin,
        autoHideDuration,
        action: showCloseButton
          ? (snackbarId) => (
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => closeSnackbar(snackbarId)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )
          : undefined,
        ...other
      }
    );
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar, closeSnackbar }}>
      {children}
    </SnackbarContext.Provider>
  );
};
